import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';

const { FiX, FiMessageSquare } = FiIcons;

const CommunicationModal = ({ isOpen, onClose, onSave, properties }) => {
  const { data } = useStore();
  const [formData, setFormData] = useState({
    propertyId: '',
    tenantId: '', // Added tenantId
    type: 'Email',
    date: new Date().toISOString().split('T')[0],
    subject: '',
    notes: '',
    status: 'Logged'
  });
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        propertyId: '',
        tenantId: '',
        type: 'Email',
        date: new Date().toISOString().split('T')[0],
        subject: '',
        notes: '',
        status: 'Logged'
      });
      setSelectedTemplate('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePropertyChange = (propId) => {
    const property = properties.find(p => p.id === propId);
    setFormData(prev => ({
      ...prev,
      propertyId: propId,
      tenantId: property?.tenantId || '' // Auto-link the current tenant of the property
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;

    const template = data.communicationTemplates.find(t => t.id === templateId);
    const property = data.properties.find(p => p.id === formData.propertyId);

    if (template) {
      let subject = template.subject;
      let content = template.content;

      if (property) {
        const replacements = {
          '[Tenant Name]': property.tenantName || 'Tenant',
          '[Property Name]': property.name,
          '[Lease End Date]': property.leaseEnd || 'TBD',
          '[Rent Rate]': property.rentRate || '0',
          '[Date]': formData.date
        };

        Object.entries(replacements).forEach(([key, value]) => {
          subject = subject.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
          content = content.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
        });
      }

      setFormData(prev => ({ ...prev, subject, notes: content }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const prop = properties.find(p => p.id === formData.propertyId);
    onSave({
      ...formData,
      tenantName: prop?.tenantName || 'N/A'
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <SafeIcon icon={FiMessageSquare} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Log Communication</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Property</label>
              <select 
                required 
                className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm"
                value={formData.propertyId}
                onChange={e => handlePropertyChange(e.target.value)}
              >
                <option value="">Select Property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Use Template</label>
              <select 
                disabled={!formData.propertyId}
                className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm disabled:opacity-50"
                value={selectedTemplate}
                onChange={e => handleTemplateSelect(e.target.value)}
              >
                <option value="">No Template</option>
                {data.communicationTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Type</label>
              <select 
                className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm"
                value={formData.type}
                onChange={e => setFormData({...formData, type: e.target.value})}
              >
                <option value="Email">Email</option>
                <option value="Call">Phone Call</option>
                <option value="SMS">SMS / Text</option>
                <option value="Notice">Official Notice</option>
                <option value="Meeting">In-Person</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Date</label>
              <input 
                required
                type="date"
                className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm"
                value={formData.date}
                onChange={e => setFormData({...formData, date: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Subject</label>
            <input 
              required
              type="text"
              placeholder="e.g. Rent Payment Confirmation"
              className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm font-medium"
              value={formData.subject}
              onChange={e => setFormData({...formData, subject: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Message Body</label>
            <textarea 
              rows="4"
              placeholder="Detail the interaction..."
              className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm leading-relaxed"
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
            />
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200">
              Save Entry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommunicationModal;